import { DayJsRange, getFinancialDataByDateRange } from '@shared/entity';
import AppTheme from '@libs/mui/AppTheme';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import RevenuCard, { RevenueStatistic } from './RevenuCard';
import { BarChart } from '@mui/x-charts/BarChart';
import { VisitDetail } from '@features/visits/entity';
import { useCurrency } from '@features/currency/currency.hooks';

type AppBarChartProps = {
  visitData: VisitDetail[];
} & DayJsRange;

const AppBarChart = (props: AppBarChartProps) => {
  const { visitData, from, to } = props;
  const intl = useIntl();
  const { preferredCurrency } = useCurrency();
  const { costs, profit, labels } = getFinancialDataByDateRange(visitData, {
    from: from.startOf('day').toDate(),
    to: to.endOf('day').toDate(),
  });
  const [selectedDay, setSelectedDay] = useState<RevenueStatistic | null>(null);

  return (
    <>
      {selectedDay && <RevenuCard selectedDay={selectedDay} onClose={() => setSelectedDay(null)} />}
      <BarChart
        height={260}
        xAxis={[{ scaleType: 'band', data: labels }]}
        series={[
          {
            data: costs,
            label: intl.formatMessage({
              defaultMessage: 'Náklady',
              id: 'appBarChart.costs',
            }),
            valueFormatter: value => `${value ? Math.round(value) : 0} ${preferredCurrency}`,
            color: AppTheme.palette.error.main,
            stack: 'finance',
          },
          {
            data: profit,
            valueFormatter: value => `${value ? Math.round(value) : 0} ${preferredCurrency}`,
            label: intl.formatMessage({
              defaultMessage: 'Zisk',
              id: 'appBarChart.revenue',
            }),
            color: AppTheme.palette.success.main,
            stack: 'finance',
          },
        ]}
        margin={{ top: 20, bottom: 20, left: 0, right: 20 }}
        onItemClick={(_, item) => {
          const profitAmount = profit[item.dataIndex];
          const costsAmount = costs[item.dataIndex];
          const revenue = costsAmount + profitAmount;
          const label = labels[item.dataIndex];

          setSelectedDay({
            profit: profitAmount,
            costs: costsAmount,
            revenue,
            label,
          });
        }}
        barLabel={(item, context) =>
          context.bar.height > 30
            ? `${item.value && Math.round(item.value)} ${preferredCurrency}`
            : null
        }
      />
    </>
  );
};

export default AppBarChart;
